import React, { useState } from "react";
import axios from "axios";
import ButtonSecondary from "../buttons/ButtonSecondary";
import Cookies from "js-cookie";
import { toast, ToastContainer } from "react-toastify";
import { Hourglass } from "react-loader-spinner";
import { useParams } from "react-router-dom";
import useFileManagerApi from "../../hooks/api/useFileManagerApi";

const ModalUploadFile = ({ isOpen, onClose, selectedFolder, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { folderNameParam } = useParams();
  const { getPresignedUploadUrl, fetchFolderStructure } = useFileManagerApi();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    setUploadSuccess(false);
  };

  const validateFile = () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return false;
    }

    const maxSize = 100 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      toast.error("File size must be less than 100MB");
      return false;
    }

    return true;
  };

  const getFolderPath = () => {
    let path = "";

    if (selectedFolder !== undefined && selectedFolder !== null) {
      if (typeof selectedFolder === "string") {
        path = selectedFolder.trim();
      } else if (Array.isArray(selectedFolder) && selectedFolder.length > 0) {
        path = selectedFolder.join("/");
      }
    } else if (folderNameParam) {
      path = folderNameParam;
    }

    const cleanPath = path.replace(/^\/+/, "").replace(/\/+$/, "");

    if (!cleanPath) {
      return "/";
    }

    return `/${cleanPath.replace(/\/+/g, "/")}/`;
  };

  const handleUploadFile = async () => {
    if (!validateFile()) {
      return;
    }

    const accessToken = Cookies.get("AccessToken");
    if (!accessToken) {
      toast.error("Authentication required. Please log in.");
      return;
    }

    setIsLoading(true);

    try {
      const fileName = selectedFile.name;
      const folderPath = getFolderPath();
      const fileSize = Math.floor(selectedFile.size); // Ensure integer

      console.log("Upload file payload:", {
        filename: fileName,
        pathtofolder: folderPath,
        filesize: fileSize,
        selectedFolder,
        folderNameParam
      });

      const presignedUrlResponse = await getPresignedUploadUrl({
        filename: fileName,
        pathtofolder: folderPath,
        filesize: fileSize
      });

      const presignedUrl = presignedUrlResponse.data.presignedUrl;

      if (!presignedUrl) {
        throw new Error("Failed to get upload URL");
      }

      const uploadResponse = await axios.put(presignedUrl, selectedFile, {
        headers: {
          'Content-Type': selectedFile.type || 'application/octet-stream',
        },
      });

      if (uploadResponse.status === 200) {
        setUploadSuccess(true);
        toast.success("File uploaded successfully!");

        // Refresh folder structure after successful upload
        try {
          await fetchFolderStructure();
        } catch (refreshError) {
          console.error("Error refreshing folder structure:", refreshError);
          // Don't show error to user, just log it
        }

        if (onUploadSuccess) {
          onUploadSuccess();
        }

        setTimeout(() => {
          handleClose();
        }, 2000);
      }
    } catch (error) {
      console.error("Upload error:", error);

      if (error.response) {
        const { status, data } = error.response;

        if (status === 413) {
          toast.error("File is too large");
        } else if (status === 400) {
          toast.error(data.error || "Invalid file or upload request");
        } else if (status === 403) {
          toast.error("You don't have permission to upload files");
        } else if (status === 404) {
          toast.error("Upload endpoint not found");
        } else {
          toast.error(data.error || "Failed to upload file. Please try again.");
        }
      } else if (error.request) {
        toast.error("Unable to connect to server. Please check your internet connection.");
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadSuccess(false);
    setIsLoading(false);
    onClose();
  };

  const handleCancel = () => {
    if (isLoading) {
      return;
    }
    handleClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 w-full h-full bg-gray-500 bg-opacity-75 flex justify-center items-center z-50">
      <div
        className={`${
          uploadSuccess ? "bg-greenLight" : "bg-white"
        } p-8 rounded-lg mx-6 min-w-[300px] max-w-[500px]`}
      >
        {uploadSuccess ? (
          <div className="text-center">
            <div className="mb-4">
              <svg
                className="mx-auto h-12 w-12 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-bodyLB text-dark4 mb-2">Success!</p>
            <p className="text-bodyMR text-dark5">
              File uploaded successfully
            </p>
          </div>
        ) : (
          <div>
            <h2 className="text-h6 text-dark4 mb-6 text-center">
              Upload a file
            </h2>

            <div className="mb-6">
              <label
                htmlFor="file-upload"
                className="block text-bodyMR text-dark4 mb-2"
              >
                Select file
              </label>
              <input
                id="file-upload"
                type="file"
                onChange={handleFileChange}
                className="block w-full text-bodyMR text-dark4 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
                disabled={isLoading}
              />
              {selectedFile && (
                <p className="mt-2 text-bodySR text-dark5">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {isLoading ? (
              <div className="flex flex-row justify-center items-center py-6">
                <Hourglass
                  visible={true}
                  height="40"
                  width="40"
                  animationDuration="2"
                  ariaLabel="hourglass-loading"
                  wrapperStyle={{}}
                  wrapperClass=""
                  colors={["#FF5400", "#FF5400"]}
                />
              </div>
            ) : (
              <div className="flex justify-center gap-4 mt-6">
                <ButtonSecondary
                  text="Upload"
                  customClass="bg-primary text-white text-bodyMR px-6 py-2 rounded-lg hover:bg-opacity-90 disabled:opacity-50"
                  onClick={handleUploadFile}
                  disabled={!selectedFile || isLoading}
                />
                <ButtonSecondary
                  text="Cancel"
                  customClass="bg-white text-dark4 text-bodyMR border border-dark4 px-6 py-2 rounded-lg hover:bg-gray-50"
                  onClick={handleCancel}
                  disabled={isLoading}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default ModalUploadFile;